import { useState, useMemo } from 'react';
import { App, Spin, Alert, Select, Checkbox } from 'antd';
import type { ButtonProps } from 'antd';
import styles from './InvoicesDashboardPage.module.css';
import useInvoicesDashboardPage, { SortField } from './useInvoicesDashboardPage';
import PageLayout from '@components/layout/PageLayout';
import VendorInvoiceComponent from '@components/vendor-invoice/VendorInvoice';
import NSPDatePicker from '@components/ui/NSPDatepicker';
import { NSPButton } from '@components/ui/NSPButton';
import useSession from '@hooks/useSession';
import VendorInvoiceSendBanner from './VendorInvoiceSendBanner';

const InvoicesDashboardPage = () => {
  const { modal } = App.useApp();
  const { isAdmin, isSuperAdmin } = useSession();
  const isApprover = isAdmin || isSuperAdmin;
  const {
    monthYearLabel,
    selectedPeriod,
    isNextDisabled,
    pickerValue,
    navigateMonth,
    handleDateChange,
    sortedData,
    sortField,
    sortDirection,
    handleSort,
    loading,
    saving,
    saveFeedback,
    setSaveFeedback,
    invoiceSendProgress,
    vendorInvoiceSendJobs,
    dismissVendorInvoiceSendJob,
    downloadVendorInvoiceSendZip,
    getVendorInvoiceSendSummary,
    handleEdit,
    handleSave,
    handleCreateVendorInvoices,
    handleCreateNspInvoices,
    isVendorApprovalPersisted,
    hasRealChanges,
    username,
  } = useInvoicesDashboardPage();
  const isSendingInvoices = invoiceSendProgress != null;

  // Selection state — only invoiceable vendors can be selected
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<number>>(new Set());
  const [invoiceType, setInvoiceType] = useState<'nsp' | 'vendor'>('nsp');

  // Bulk approval state
  const [bulkApprovedVendorIds, setBulkApprovedVendorIds] = useState<Set<number>>(new Set());

  const readyForApprovalVendors = useMemo(
    () => sortedData.filter((v) => {
      const status = v.approval_status;
      return status === 'Ready' && v.invoice_status !== 'sent';
    }),
    [sortedData]
  );

  const allReadyApproved =
    readyForApprovalVendors.length > 0 &&
    readyForApprovalVendors.every((v) => bulkApprovedVendorIds.has(v.vendor_id!));

  const someReadyApproved =
    readyForApprovalVendors.some((v) => bulkApprovedVendorIds.has(v.vendor_id!)) &&
    !allReadyApproved;

  const handleBulkApprove = (checked: boolean) => {
    const newBulkIds = checked
      ? new Set(readyForApprovalVendors.map((v) => v.vendor_id!))
      : new Set<number>();
    setBulkApprovedVendorIds(newBulkIds);
    readyForApprovalVendors.forEach((v) => {
      if (v.vendor_id != null) {
        // Skip vendors that are already persisted as approved on the server
        if (isVendorApprovalPersisted(v.vendor_id)) return;
        handleEdit(v.vendor_id, 'invoice_approved', checked);
        if (checked && username) {
          handleEdit(v.vendor_id, 'invoice_computed_approval_signature', username);
        } else if (!checked) {
          handleEdit(v.vendor_id, 'invoice_computed_approval_signature', null);
        }
      }
    });
  };

  // Already-sent invoices stay selectable so they can be re-sent as many times as needed.
  const invoiceableVendors = useMemo(
    () => sortedData.filter((v) => {
      return (
        v.invoice_status === 'sent' ||
        v.approval_status === 'Approved' ||
        isVendorApprovalPersisted(v.vendor_id!)
      );
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedData, isVendorApprovalPersisted]
  );

  // Select All only picks invoices not yet sent, so it can never re-email vendors by accident —
  // a sent invoice is re-sent only when its own checkbox is ticked.
  const unsentInvoiceableVendors = useMemo(
    () => invoiceableVendors.filter((v) => v.invoice_status !== 'sent'),
    [invoiceableVendors]
  );

  const allInvoiceableSelected =
    unsentInvoiceableVendors.length > 0 &&
    unsentInvoiceableVendors.every((v) => selectedVendorIds.has(v.vendor_id!));

  const someSelected = selectedVendorIds.size > 0 && !allInvoiceableSelected;

  const handleSelectAll = (checked: boolean) => {
    setSelectedVendorIds(
      checked ? new Set(unsentInvoiceableVendors.map((v) => v.vendor_id!)) : new Set()
    );
  };

  const handleToggleVendor = (vendorId: number, checked: boolean) => {
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(vendorId);
      else next.delete(vendorId);
      return next;
    });
  };

  const handleCreateInvoices = () => {
    const proceed = () => {
      if (invoiceType === 'vendor') {
        // Vendors whose email failed stay selected so the user can retry them right away
        handleCreateVendorInvoices(selectedVendorIds).then((unsentVendorIds) => setSelectedVendorIds(new Set(unsentVendorIds)));
      } else {
        handleCreateNspInvoices(selectedVendorIds).then(() => setSelectedVendorIds(new Set()));
      }
    };

    // Count vendors not yet approved (not sent)
    const unapprovedCount = sortedData.filter(
      (v) => v.invoice_status !== 'sent' &&
        v.approval_status !== 'Approved' &&
        !isVendorApprovalPersisted(v.vendor_id!)
    ).length;

    // Count invoiceable vendors not yet sent that weren't selected
    const unselectedInvoiceableCount = unsentInvoiceableVendors.filter(
      (v) => v.vendor_id != null && !selectedVendorIds.has(v.vendor_id)
    ).length;

    // Selected vendors whose invoice was already sent — sending a vendor invoice emails them again
    const alreadySentSelectedCount = invoiceType === 'vendor'
      ? new Set(
        sortedData
          .filter((v) => v.vendor_id != null && v.invoice_status === 'sent' && selectedVendorIds.has(v.vendor_id))
          .map((v) => v.vendor_id)
      ).size
      : 0;

    if (unapprovedCount === 0 && unselectedInvoiceableCount === 0 && alreadySentSelectedCount === 0) {
      proceed();
      return;
    }

    const lines: string[] = [];
    if (alreadySentSelectedCount > 0) {
      const sentLabel = alreadySentSelectedCount === 1
        ? '1 selected vendor invoice was already sent and will be emailed to the vendor again.'
        : `${alreadySentSelectedCount} selected vendor invoices were already sent and will be emailed to those vendors again.`;
      lines.push(sentLabel);
    }
    if (unapprovedCount > 0) {
      const unapprovedLabel = unapprovedCount === 1 ? '1 vendor invoice is' : `${unapprovedCount} vendor invoices are`;
      lines.push(`${unapprovedLabel} not yet approved.`);
    }
    if (unselectedInvoiceableCount > 0) {
      const unselectedLabel = unselectedInvoiceableCount === 1 ? '1 approved vendor is' : `${unselectedInvoiceableCount} approved vendors are`;
      lines.push(`${unselectedLabel} not selected.`);
    }

    modal.confirm({
      title: 'Some invoices need attention',
      content: lines.join(' ') + ' Do you want to continue?',
      okText: 'Yes',
      cancelText: 'Cancel',
      okButtonProps: { 'data-testid': 'modal-ok-button' } as ButtonProps,
      cancelButtonProps: { 'data-testid': 'modal-cancel-button' } as ButtonProps,
      onOk: proceed,
    });
  };

  const sortIcon = (field: SortField | null) => {
    if (sortField !== field) return <span className={styles.sortIcon}>⇅</span>;
    return (
      <span className={`${styles.sortIcon} ${styles.active}`}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <PageLayout>
      <div className={styles.pageContent}>
        {vendorInvoiceSendJobs.map((job) => (
          <VendorInvoiceSendBanner
            key={job.jobId}
            job={job}
            summary={getVendorInvoiceSendSummary(job)}
            canDownloadZip={job.monthYear === selectedPeriod}
            onDownloadZip={downloadVendorInvoiceSendZip}
            onDismiss={dismissVendorInvoiceSendJob}
          />
        ))}
        {saveFeedback && (
          <Alert
            message={saveFeedback.status === 'success' ? 'Save Successful!' : 'Save Failed!'}
            description={saveFeedback.message}
            type={saveFeedback.status}
            showIcon
            closable
            onClose={() => setSaveFeedback(null)}
            style={{ marginBottom: '16px' }}
            data-testid="save-feedback-alert"
            data-type={saveFeedback.status}
          />
        )}
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <h1 className={styles.tableTitle} data-testid="page-title">
              {`Invoices by Vendor for ${monthYearLabel}`}
            </h1>
            <div className={styles.headerControls}>
              <Select
                value={invoiceType}
                onChange={setInvoiceType}
                style={{ width: 160 }}
                size="small"
                className={styles.invoiceTypeSelect}
                options={[
                  { value: 'nsp', label: 'NSP Invoice' },
                  { value: 'vendor', label: 'Vendor Invoice' },
                ].map((o) => ({ ...o, 'data-testid': 'invoice-type-option', 'data-value': o.value }))}
                data-testid="invoice-type-select"
              />
              <NSPButton
                size="small"
                disabled={selectedVendorIds.size === 0 || isSendingInvoices}
                loading={isSendingInvoices}
                onClick={handleCreateInvoices}
                data-testid="create-invoices-button"
              >
                Create Invoices
              </NSPButton>
              <div className={styles.headerControlsSpacer} />
              <NSPDatePicker
                picker="month"
                format="MMM YYYY"
                allowClear={false}
                value={pickerValue}
                onChange={handleDateChange}
                showNavigation={true}
                showCalendarIcon={false}
                onNavigateLeft={() => navigateMonth(-1)}
                onNavigateRight={() => navigateMonth(1)}
                isNextDisabled={isNextDisabled}
              />
              <NSPButton onClick={handleSave} loading={saving && !isSendingInvoices} disabled={!hasRealChanges || isSendingInvoices} data-testid="save-changes-button">
                Save All Changes
              </NSPButton>
            </div>
          </div>

          {loading ? (
            <div className={styles.loadingContainer} data-testid="table-loading">
              <Spin size="large" />
            </div>
          ) : (
            <table className={styles.vendorTable} data-testid="invoices-table" data-row-count={sortedData.length}>
              <thead>
                <tr>
                  <th style={{ width: 48, textAlign: 'center' }}>
                    <Checkbox
                      checked={allInvoiceableSelected}
                      indeterminate={someSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={(unsentInvoiceableVendors.length === 0 && selectedVendorIds.size === 0) || isSendingInvoices}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th style={{ width: 48 }}></th>
                  <th
                    style={{ minWidth: 140 }}
                    className={styles.sortableHeader}
                    onClick={() => handleSort('vendor_name')}
                    data-testid="sort-vendor"
                    data-sort={sortField === 'vendor_name' ? sortDirection : 'none'}
                  >
                    Vendor{sortIcon('vendor_name')}
                  </th>
                  <th className={styles.textCenter}>Subtotal (Before Tax)</th>
                  <th className={styles.textCenter}>Tax</th>
                  <th className={styles.textCenter}>Total Invoice (Incl. Tax)</th>
                  <th
                    className={`${styles.textCenter} ${styles.sortableHeader}`}
                    onClick={() => handleSort('invoice_status')}
                    data-testid="sort-status"
                    data-sort={sortField === 'invoice_status' ? sortDirection : 'none'}
                  >
                    Status{sortIcon('invoice_status')}
                  </th>
                  <th className={styles.textCenter}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      {isApprover && (
                        <Checkbox
                          checked={allReadyApproved}
                          indeterminate={someReadyApproved}
                          disabled={readyForApprovalVendors.length === 0}
                          onChange={(e) => handleBulkApprove(e.target.checked)}
                          data-testid="bulk-approve-checkbox"
                        />
                      )}
                      <span>Approved</span>
                    </div>
                  </th>
                  <th className={styles.textCenter}>Invoices</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((vendor) => (
                  <VendorInvoiceComponent
                    key={vendor.is_counterparty_invoice
                      ? `cp-${vendor.primary_vendor_name ?? vendor.co_vendor_invoice_id}-${vendor.vendor_id}`
                      : (vendor.vendor_id?.toString() ?? vendor.vendor_name)}
                    vendor={vendor}
                    monthYear={selectedPeriod}
                    onEdit={handleEdit}
                    isVendorApprovalPersisted={isVendorApprovalPersisted}
                    username={username}
                    isApprover={isApprover}
                    isSelected={vendor.vendor_id != null && selectedVendorIds.has(vendor.vendor_id)}
                    onToggleSelect={handleToggleVendor}
                    bulkApproved={vendor.vendor_id != null && bulkApprovedVendorIds.has(vendor.vendor_id)}
                    onBulkApprovedRemove={(vendorId) => {
                      setBulkApprovedVendorIds((prev) => {
                        const next = new Set(prev);
                        next.delete(vendorId);
                        return next;
                      });
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default InvoicesDashboardPage;
